import { Link } from 'react-router-dom';
import { DocSection, Callout, kbd } from '../../../pages/shell';
import { DocShot } from '../../DocShot';

export const toc = [
  { id: 'modes', label: 'Search modes' },
  { id: 'composer', label: 'The composer' },
  { id: 'filters', label: 'Scope, People & Exclude filters' },
  { id: 'mention', label: 'Search by a named person' },
  { id: 'recent-quota', label: 'Recent searches & quota chip' },
  { id: 'results-toolbar', label: 'Results & the results toolbar' },
  { id: 'card-actions', label: 'Result-card actions' },
  { id: 'right-click', label: 'Right-click menu' },
  { id: 'panel', label: 'In the Premiere panel' },
];

export default function Body() {
  return (
    <>
      <DocSection id="modes" title="Search modes">
        <p>
          Type a query and ClipRadar searches your whole library in place — no separate index step,
          no waiting. A segmented switch at the top of the composer picks how the query is read:
        </p>
        <DocShot
          slug="search-tab"
          shot="composer"
          alt="Annotated Search bar with numbered callouts"
          caption="The Search bar at the top of the Search tab."
          callouts={[
            { n: 1, label: 'Search mode — Visuals, Speech, or Ultra' },
            { n: 2, label: 'Composer — type a query; Enter to search, Shift+Enter for a new line' },
            { n: 3, label: 'Scope filter — limit the search to a collection, folder, or selection' },
            { n: 4, label: 'People filter — narrow to one or more named people' },
            { n: 5, label: 'Exclude filter — hide terms you don’t want back' },
            { n: 6, label: 'Recent searches — one-click re-run of past queries' },
            { n: 7, label: 'Search quota chip — remaining trial searches (hidden on unlimited licenses)' },
          ]}
        />
        <ul>
          <li>
            <span style={kbd}>Visuals</span> — semantic search over what the pixels show. Describe
            the shot, not a sentence. Returns one result per <strong>scene</strong>. See{' '}
            <Link to="/docs/concepts/visual-search">Visual search</Link> for how it reads footage and{' '}
            <Link to="/docs/guides/search-best-practices">Get better search results</Link> for phrasing.
          </li>
          <li>
            <span style={kbd}>Speech</span> — full-text search over transcripts. Titled "Search
            transcripts" in the mode switch. See{' '}
            <Link to="/docs/concepts/speech-search">Speech search</Link>.
          </li>
          <li>
            <span style={kbd}>Ultra</span> — a separate pill after a divider. Fuses visuals, speech,
            and people into one ranked result set from a single sentence. See{' '}
            <Link to="/docs/concepts/ultra-search">Ultra Search</Link> for how it parses a query into{' '}
            <span style={kbd}>chips</span> and what to do if it's greyed out on your GPU.
          </li>
        </ul>
      </DocSection>

      <DocSection id="composer" title="The composer">
        <p>
          The composer is a tall, auto-growing text box. <span style={kbd}>Enter</span> submits the
          search; <span style={kbd}>Shift+Enter</span> adds a line break instead. The keycap arrow
          button submits the same way; an <span style={kbd}>X</span> button clears the box once you've
          typed something. In Ultra mode the placeholder changes to prompt for more detail — who's on
          screen, what they say, how it's framed.
        </p>
        <p>
          A status line under the composer reports what's happening: a parsing message while Ultra
          reads your sentence, or a plain-search fallback note if it can't run right now.{' '}
          <span style={kbd}>Esc</span> backs out of Ultra mode (or closes the @-mention list first, if
          one is open).
        </p>
      </DocSection>

      <DocSection id="filters" title="Scope, People & Exclude filters">
        <p>
          Three filters sit above the composer. <strong>Scope</strong> limits the search to a{' '}
          <Link to="/docs/interface/media">collection, folder, or selection</Link> instead of your
          whole library, and stays active even while Ultra is on. <strong>People</strong> and{' '}
          <strong>Exclude</strong> narrow results to (or away from) named people and terms — but they
          go inert and dimmed the moment Ultra is toggled on, because Ultra reads that same intent out
          of your sentence as <span style={kbd}>Person</span> and <span style={kbd}>Not</span> chips
          instead. Turn Ultra off to use the People and Exclude filters directly again.
        </p>
      </DocSection>

      <DocSection id="mention" title="Search by a named person">
        <p>
          Start typing a person&rsquo;s name in the composer and a typeahead suggests matching named
          people; <span style={kbd}>Enter</span> or a click accepts one as an inline pill in your query.
          Typing <span style={kbd}>@</span> is the explicit way to open that same people list.{' '}
          <span style={kbd}>Backspace</span> on an empty query removes the most recently added person
          pill. This works alongside Ultra — adding a person is one more way to feed it a{' '}
          <span style={kbd}>Person</span> chip.
        </p>
      </DocSection>

      <DocSection id="recent-quota" title="Recent searches & quota chip">
        <p>
          Below the composer, pills show your recent queries for a one-click re-run, with a{' '}
          <strong>Clear</strong> button to wipe the list. Turn this off in{' '}
          <Link to="/docs/interface/settings#search-prefs">Settings → Search preferences</Link> if you'd
          rather not see it.
        </p>
        <p>
          A <strong>search quota chip</strong> — "N / M left" — appears for metered trial licenses,
          turning amber at 5 remaining and red at 0. Unlimited licenses never show it. See{' '}
          <Link to="/docs/concepts/licensing">Licensing & activation</Link> for what happens when the
          trial runs out.
        </p>
      </DocSection>

      <DocSection id="results-toolbar" title="Results & the results toolbar">
        <p>
          Every search adds a new <strong>result block</strong> — the query plus its cards — so you can
          run several searches and compare them without losing earlier results. A toolbar above the
          cards carries the shared controls:
        </p>
        <DocShot
          slug="search-tab"
          shot="results"
          alt="The results grid with the results toolbar above it"
          callouts={[
            { n: 1, label: 'Results count — how many searches are stacked in view' },
            { n: 2, label: 'Thumbnail-size zoom slider' },
            { n: 3, label: 'Export N to timeline (desktop) / Place N (panel)' },
            { n: 4, label: 'Deselect' },
            { n: 5, label: 'Clear All — confirms before wiping every result block' },
          ]}
        />
        <p>
          Select cards with their checkboxes, then <strong>Export N to timeline</strong> in the desktop
          app (a timeline XML you import into Premiere, Resolve, or Final Cut — see{' '}
          <Link to="/docs/guides/build-a-rough-cut">From search results to a rough cut</Link>) or{' '}
          <strong>Place N</strong> in the Premiere panel to drop them straight onto the open sequence.
        </p>
      </DocSection>

      <DocSection id="card-actions" title="Result-card actions">
        <p>
          Hover a card to reveal its action bar. What's on it depends on the card's kind and which
          shell you're in:
        </p>
        <DocShot
          slug="search-tab"
          shot="card-actions"
          alt="A video result card with its hover action bar showing Adjust and Export"
          caption="Desktop video card, idle state."
        />
        <ul>
          <li>
            <strong>Desktop video card:</strong> <span style={kbd}>Adjust</span> (trim the in/out
            before export) and <span style={kbd}>Export</span>. After exporting, the bar swaps in{' '}
            <span style={kbd}>Show in folder</span> and <span style={kbd}>Re-export</span>.
          </li>
          <li>
            <strong>Desktop speech card:</strong> the same bar, but <span style={kbd}>Adjust</span> is
            replaced by <span style={kbd}>Show transcript</span>, which opens the full transcript
            scrolled to the matched line, with segments you can select and add to a timeline.
          </li>
          <li>
            <strong>Desktop image card:</strong> <span style={kbd}>Match similar</span> and{' '}
            <span style={kbd}>Open original</span>.
          </li>
        </ul>
        <DocShot
          slug="search-tab"
          shot="speech-card"
          alt="A speech result card showing the matched transcript line"
          caption="A Speech-mode result card — the matched line is highlighted in context."
        />
      </DocSection>

      <DocSection id="right-click" title="Right-click menu">
        <p>
          Right-click any card for more actions: <span style={kbd}>Match similar</span> (video),{' '}
          <span style={kbd}>Open original file</span> / <span style={kbd}>Show in folder</span>{' '}
          (image), <span style={kbd}>Relink missing file…</span> for a card whose source moved, and{' '}
          <span style={kbd}>Remove from results…</span> to purge one segment from the block. When
          results are filtered to a person, an extra <span style={kbd}>"This isn't &lt;Person&gt;"</span>{' '}
          entry removes a wrongly-attributed face from that clip's time window — see{' '}
          <Link to="/docs/guides/find-and-name-people">Find & name people</Link> for the full
          correction workflow.
        </p>
      </DocSection>

      <DocSection id="panel" title="In the Premiere panel">
        <p>
          The Search tab works the same way inside the <Link to="/docs/platforms/premiere-panel">Premiere
          panel</Link>, with a few panel-only additions: a camera button grabs the current{' '}
          <strong>Program-monitor</strong> frame as an image search, and{' '}
          <span style={kbd}>Ctrl+V</span> pastes any copied image straight into a visual search.
        </p>
        <DocShot
          slug="search-tab"
          shot="panel-row"
          alt="A result card row in the Premiere panel with its five icon actions"
          panel
          caption="Panel video card: five icon actions in place of the desktop's hover bar."
        />
        <p>
          Panel card actions are icon-only to fit the narrow width. A video card gets{' '}
          <span style={kbd}>Add to timeline (video+audio)</span>,{' '}
          <span style={kbd}>Add to timeline (video only)</span>,{' '}
          <span style={kbd}>Open in Source Monitor</span>, <span style={kbd}>Open in Explorer</span>,
          and <span style={kbd}>Find similar</span>; a speech card drops to three (
          <span style={kbd}>Add to timeline</span>, <span style={kbd}>Open in Source Monitor</span>,{' '}
          <span style={kbd}>Open transcript</span>); an image card keeps{' '}
          <span style={kbd}>Find similar</span> and <span style={kbd}>Open in Explorer</span>.
        </p>
        <Callout kind="tip">
          A <strong>placement mode</strong> toggle in the panel toolbar — <span style={kbd}>Smart</span>{' '}
          (a free track, non-destructive, the default), <span style={kbd}>Insert</span> (ripples at the
          playhead), or <span style={kbd}>Append</span> (sequence end) — applies to every{' '}
          <span style={kbd}>Add to timeline</span> button until you change it.
        </Callout>
      </DocSection>
    </>
  );
}
